import React, { useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import LinearProgress from "@material-ui/core/LinearProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Importação de base a partir de planilha.
 *
 * O arquivo é lido no navegador e enviado como texto. Assim dá para mostrar a
 * prévia antes de confirmar — importar 500 contatos às cegas e descobrir o
 * erro depois é o pior desfecho possível, porque desfazer custa mais que
 * refazer.
 */
const useStyles = makeStyles((theme) => ({
  bloco: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  regra: { display: "flex", gap: theme.spacing(1), marginTop: 4 },
  arquivo: { display: "none" },
  previa: { maxHeight: 240, overflow: "auto", marginTop: theme.spacing(1) },
  resumo: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  kpi: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
  },
  valor: { fontSize: 24, fontWeight: 700, lineHeight: 1.2 },
  erros: { maxHeight: 200, overflow: "auto" },
}));

const ClientImportModal = ({ open, onClose, onImported }) => {
  const classes = useStyles();
  const inputRef = useRef(null);

  const [csv, setCsv] = useState("");
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [previa, setPrevia] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const limpar = () => {
    setCsv("");
    setNomeArquivo("");
    setPrevia([]);
    setResultado(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const fechar = () => {
    limpar();
    onClose();
  };

  const baixarModelo = async () => {
    try {
      const { data } = await api.get("/clients/import/template", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "modelo-importacao-clientes.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const escolherArquivo = (evento) => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    setNomeArquivo(arquivo.name);
    setResultado(null);

    const leitor = new FileReader();
    leitor.onload = () => {
      const texto = String(leitor.result || "");
      setCsv(texto);

      // Prévia simples, só para conferir se as colunas casaram. A validação
      // de verdade acontece no servidor.
      const linhas = texto.split(/\r?\n/).filter((l) => l.trim());
      setPrevia(linhas.slice(0, 6).map((l) => l.split(/[;,]/)));
    };
    // UTF-8 explícito: sem isso, arquivo salvo pelo Excel chega com acento
    // trocado e o nome do cliente entra errado na base.
    leitor.readAsText(arquivo, "UTF-8");
  };

  const importar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post("/clients/import", { csv });
      setResultado(data);
      if (data.created || data.updated) {
        toast.success(
          data.updated
            ? `${data.created} criados · ${data.updated} mesclados em cadastros existentes`
            : `${data.created} clientes importados`
        );
        onImported && onImported();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={fechar} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Importar base de clientes</DialogTitle>
      <DialogContent dividers>
        {!resultado && (
          <>
            <div className={classes.bloco}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                1. Baixe o modelo
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Colunas: nome, tipo, telefone, email, origem, tag, status. O
                modelo já vem com exemplos preenchidos.
              </Typography>
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={baixarModelo}
                >
                  Baixar modelo CSV
                </Button>
              </Box>
            </div>

            <div className={classes.bloco}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Como cada coluna é tratada
              </Typography>
              <div className={classes.regra}>
                <Chip size="small" label="telefone" />
                <Typography variant="caption" color="textSecondary">
                  Obrigatório. Aceita qualquer formato e grava como
                  55&nbsp;+&nbsp;DDD&nbsp;+&nbsp;número.
                </Typography>
              </div>
              <div className={classes.regra}>
                <Chip size="small" label="origem" />
                <Typography variant="caption" color="textSecondary">
                  Precisa existir no CRM. Sem correspondência, o cliente entra
                  sem origem.
                </Typography>
              </div>
              <div className={classes.regra}>
                <Chip size="small" label="status" />
                <Typography variant="caption" color="textSecondary">
                  Mesma regra da origem.
                </Typography>
              </div>
              <div className={classes.regra}>
                <Chip size="small" label="tag" color="primary" />
                <Typography variant="caption" color="textSecondary">
                  Se não existir, <strong>é criada</strong>. Aceita várias
                  separadas por vírgula.
                </Typography>
              </div>
              <div className={classes.regra}>
                <Chip size="small" label="email" />
                <Typography variant="caption" color="textSecondary">
                  Opcional. Em branco continua em branco.
                </Typography>
              </div>
            </div>

            <div className={classes.bloco}>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                2. Envie sua planilha
              </Typography>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className={classes.arquivo}
                onChange={escolherArquivo}
              />
              <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PublishIcon />}
                  onClick={() => inputRef.current?.click()}
                >
                  Escolher arquivo
                </Button>
                {nomeArquivo && (
                  <Typography variant="caption" color="textSecondary">
                    {nomeArquivo}
                  </Typography>
                )}
              </Box>

              {previa.length > 1 && (
                <div className={classes.previa}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {previa[0].map((c, i) => (
                          <TableCell key={i} style={{ fontWeight: 700 }}>
                            {c}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previa.slice(1).map((linha, i) => (
                        <TableRow key={i}>
                          {linha.map((c, j) => (
                            <TableCell key={j}>{c}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="textSecondary">
                    Mostrando as primeiras linhas apenas para conferir as
                    colunas.
                  </Typography>
                </div>
              )}
            </div>

            {enviando && <LinearProgress />}
          </>
        )}

        {resultado && (
          <>
            <div className={classes.resumo}>
              <div className={classes.kpi}>
                <div className={classes.valor} style={{ color: "#0D9488" }}>
                  {resultado.created}
                </div>
                <Typography variant="caption" color="textSecondary">
                  criados
                </Typography>
              </div>
              <div className={classes.kpi}>
                <div className={classes.valor} style={{ color: "#A16207" }}>
                  {resultado.updated}
                </div>
                <Typography variant="caption" color="textSecondary">
                  mesclados
                </Typography>
              </div>
              <div className={classes.kpi}>
                <div
                  className={classes.valor}
                  style={{ color: resultado.failed ? "#DC2626" : undefined }}
                >
                  {resultado.failed}
                </div>
                <Typography variant="caption" color="textSecondary">
                  recusados
                </Typography>
              </div>
            </div>

            {resultado.createdTags?.length > 0 && (
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Tags criadas nesta importação:
                </Typography>
                <Box mt={0.5}>
                  {resultado.createdTags.map((t) => (
                    <Chip
                      key={t}
                      size="small"
                      label={t}
                      color="primary"
                      style={{ marginRight: 4, marginBottom: 4 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* "1 atualizado" fazia o operador procurar o nome da planilha e
                não achar ninguém — porque a linha tinha sido absorvida por um
                cadastro de outro nome. Aqui ele vê em quem. */}
            {resultado.merged?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Linhas mescladas em cadastros existentes
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  O telefone ou e-mail já estava na base. O nome da planilha
                  não substitui o que já existia — só campos vazios foram
                  preenchidos.
                </Typography>
                <div className={classes.erros}>
                  <Table size="small">
                    <TableBody>
                      {resultado.merged.map((m, i) => (
                        <TableRow key={i}>
                          <TableCell style={{ width: 70 }}>
                            linha {m.line}
                          </TableCell>
                          <TableCell>{m.importedName}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="textSecondary">
                              foi para
                            </Typography>{" "}
                            <strong>{m.existingName}</strong>{" "}
                            <Typography variant="caption" color="textSecondary">
                              (#{m.existingId})
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {m.filled?.length ? (
                              <Typography variant="caption" style={{ color: "#A16207" }}>
                                preencheu: {m.filled.join(", ")}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                nada alterado
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Box>
            )}

            {resultado.errors?.length > 0 ? (
              <>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Linhas não importadas
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  O número é o da linha na sua planilha — corrija e importe só
                  essas.
                </Typography>
                <div className={classes.erros}>
                  <Table size="small">
                    <TableBody>
                      {resultado.errors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell style={{ width: 70 }}>
                            linha {e.line}
                          </TableCell>
                          <TableCell>{e.name || "—"}</TableCell>
                          <TableCell style={{ color: "#DC2626" }}>
                            {e.messages.join("; ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <Box textAlign="center" py={2}>
                <CheckCircleOutlineIcon
                  style={{ fontSize: 40, color: "#0D9488" }}
                />
                <Typography variant="body2">
                  Todas as linhas foram importadas.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {resultado ? (
          <>
            <Button onClick={limpar}>Importar outra</Button>
            <Button variant="contained" color="primary" onClick={fechar}>
              Concluir
            </Button>
          </>
        ) : (
          <>
            <Button onClick={fechar} disabled={enviando}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={importar}
              disabled={!csv || enviando}
            >
              {enviando ? "Importando…" : "Importar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientImportModal;
